<script lang="ts">
	import * as m from '$lib/paraglide/messages';
	import { goto } from '$app/navigation';
	import { Search } from '@lucide/svelte';
	import { wikiCategoryLabel } from '../../logic/category-labels';
	import { searchWiki, type WikiSearchRecord } from '../../logic/wiki-search';

	let {
		records,
		onNavigate
	}: {
		records: WikiSearchRecord[];
		/** Wywoływany po wyborze wyniku — np. zamknięcie mobilnego Sheeta. */
		onNavigate?: () => void;
	} = $props();

	const uid = $props.id();
	const listboxId = `wiki-search-listbox-${uid}`;

	let query = $state('');
	let activeIndex = $state(0);
	let focused = $state(false);
	let container: HTMLDivElement;

	const results = $derived(searchWiki(records, query));
	const open = $derived(focused && query.trim().length > 0);

	$effect(() => {
		void results;
		activeIndex = 0;
	});

	function navigate(record: WikiSearchRecord) {
		query = '';
		focused = false;
		onNavigate?.();
		void goto(record.href);
	}

	function onKeydown(event: KeyboardEvent) {
		if (!open) return;
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			activeIndex = Math.min(activeIndex + 1, results.length - 1);
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			activeIndex = Math.max(activeIndex - 1, 0);
		} else if (event.key === 'Enter') {
			const hit = results[activeIndex];
			if (hit !== undefined) {
				event.preventDefault();
				navigate(hit);
			}
		} else if (event.key === 'Escape') {
			query = '';
		}
	}

	function onFocusout(event: FocusEvent) {
		if (!container.contains(event.relatedTarget as Node | null)) {
			focused = false;
		}
	}
</script>

<div class="wiki-search" bind:this={container} onfocusout={onFocusout}>
	<div class="search-field">
		<Search size={14} aria-hidden="true" />
		<input
			type="search"
			role="combobox"
			aria-label={m.wiki_search_label()}
			aria-expanded={open}
			aria-controls={listboxId}
			aria-autocomplete="list"
			aria-activedescendant={open && results[activeIndex] !== undefined
				? `${listboxId}-option-${activeIndex}`
				: undefined}
			placeholder={m.wiki_search_placeholder()}
			autocomplete="off"
			bind:value={query}
			onfocus={() => (focused = true)}
			onkeydown={onKeydown}
		/>
	</div>

	{#if open}
		<div class="search-panel" id={listboxId} role="listbox" aria-label={m.wiki_search_label()}>
			{#each results as result, index (result.pageId)}
				<a
					id={`${listboxId}-option-${index}`}
					role="option"
					aria-selected={index === activeIndex}
					class="search-result"
					class:active={index === activeIndex}
					href={result.href}
					onmousedown={(event) => {
						event.preventDefault();
						navigate(result);
					}}
				>
					<span class="result-title">
						<span>{result.title}</span>
						{#if result.fallback}<span class="lang-chip">EN</span>{/if}
					</span>
					<span class="result-category">{wikiCategoryLabel(result.category)}</span>
				</a>
			{:else}
				<p class="no-results">{m.wiki_search_no_results()}</p>
			{/each}
		</div>
	{/if}
</div>

<style>
	.wiki-search {
		position: relative;
	}
	.search-field {
		display: flex;
		align-items: center;
		gap: 0.45rem;
		border: 1px solid var(--v3-border);
		border-radius: 0.6rem;
		padding: 0.35rem 0.6rem;
		color: var(--v3-muted);
		background: var(--v3-bg);
		transition: border-color 0.2s ease;
	}
	.search-field:focus-within {
		border-color: var(--v3-border-strong);
		color: var(--v3-text);
	}
	.search-field input {
		flex: 1;
		min-width: 0;
		border: 0;
		outline: 0;
		background: transparent;
		font-size: 0.85rem;
		color: var(--v3-text);
	}
	.search-field input::placeholder {
		color: var(--v3-muted);
	}
	.search-panel {
		position: absolute;
		top: calc(100% + 0.35rem);
		left: 0;
		right: 0;
		z-index: 30;
		display: flex;
		flex-direction: column;
		gap: 0.125rem;
		padding: 0.35rem;
		border: 1px solid var(--v3-border);
		border-radius: 0.75rem;
		background: var(--v3-bg);
		box-shadow: 0 12px 32px rgb(0 0 0 / 0.12);
		max-height: 20rem;
		overflow-y: auto;
	}
	.search-result {
		display: flex;
		flex-direction: column;
		gap: 0.1rem;
		padding: 0.4rem 0.6rem;
		border-radius: 0.5rem;
		text-decoration: none;
	}
	.search-result:hover,
	.search-result.active {
		background: var(--v3-surface);
	}
	.result-title {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.5rem;
		font-size: 0.88rem;
		font-weight: 600;
		color: var(--v3-text);
	}
	.result-category {
		font-size: 0.7rem;
		font-weight: 700;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--v3-muted);
	}
	.lang-chip {
		font-size: 0.62rem;
		font-weight: 700;
		letter-spacing: 0.06em;
		color: var(--v3-muted);
		border: 1px solid var(--v3-border);
		border-radius: 9999px;
		padding: 0.05rem 0.4rem;
		flex-shrink: 0;
	}
	.no-results {
		margin: 0;
		padding: 0.4rem 0.6rem;
		font-size: 0.85rem;
		color: var(--v3-muted);
	}
</style>
